import React, { createContext, useState, useEffect, useCallback } from 'react';
import * as authService from '../services/authService';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const userData = await authService.getMe();
      setUser(userData);
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
    };
    window.addEventListener('auth-logout', handleLogout);
    return () => window.removeEventListener('auth-logout', handleLogout);
  }, []);

  const login = async (username, password) => {
    const { token, user: userData } = await authService.login(username, password);
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const hasRole = (roleName) => {
    if (!user) return false;
    if (user.role) return user.role === roleName;
    if (user.roles) return user.roles.some((r) => r.name === roleName || r === roleName);
    return false;
  };

  const isAuthenticated = !!user;
  const canEditGrid = isAuthenticated && hasRole('admin');
  const canEditEpisodes = isAuthenticated && (hasRole('admin') || hasRole('editor'));

  const value = {
    user,
    loading,
    isAuthenticated,
    canEdit: canEditGrid,
    canEditGrid,
    canEditEpisodes,
    login,
    logout,
    hasRole,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
